import { FC, ReactNode, useCallback, useMemo, useState } from 'react';

import { useRouter, useRouterState } from '@tanstack/react-router';

import { PageNames } from '@/constants';
import { useLayoutDBSync } from '@/hooks';
import {
  getLayoutsSortedByName,
  getNextLayoutId,
  getPreviousLayoutId,
} from '@/hooks/shortcutHooks/layoutCycleNavigation.ts';
import { useGamepadControl } from '@/providers/gamepadProvider/useGamepad';
import { useLayoutStorage } from '@/stores';

import { JoystickActionConfig, JoystickConfigMap } from './gamepadProvider/gamepad.types';
import { GamepadLayout } from './gamepadProvider/gamepadLayout';
import {
  activate,
  gamepadNavigateBetweenCameraStreams,
  toggleFocusedCameraSelector,
  toggleFocusedCameraStreamFullscreen,
} from './gamepadProvider/gamepadProvider.help';
import { GamepadContext, GamepadContextValue } from './gamepadProvider/useGamepadContext';

/**
 * L2/R2 (`moveToPrevVideo` / `moveToNextVideo`): handled **here** in the global RAF loop, unlike
 * Alt+arrow which runs through `CameraStreamWidgetWrapper` + `scopeRef`. Raw `moveFocus` cannot
 * reliably pair with fullscreen exit because focus is often `body` after `exitFullscreen` when
 * navigating with gamepad only — see `gamepadNavigateBetweenCameraStreams` and the anchor logic
 * in `moveFocusCameraWidgetExitFullscreenThenNavigate`.
 *
 * `repeat: false` matches keyboard (`ev.repeat` ignored): one stream step per trigger pull.
 *
 * LB/RB (`layout.nextLayout` / `layout.previousLayout`): same cycle as Ctrl+Shift+Left/Right — see
 * `layoutCycleNavigation.ts` and `useLayoutNavigationHotkeys`. Only registered on
 * `/(dashboard)/layouts/$id`, matching keyboard layout hotkeys (grid-scoped there).
 */
const GLOBAL_INPUT_MAP: JoystickConfigMap = {
  [GamepadLayout.global.moveToNextVideo]: {
    handler: () => gamepadNavigateBetweenCameraStreams(1),
    repeat: true,
    intervalMs: 300,
  } satisfies JoystickActionConfig,
  [GamepadLayout.global.moveToPrevVideo]: {
    handler: () => gamepadNavigateBetweenCameraStreams(-1),
    repeat: true,
    intervalMs: 300,
  } satisfies JoystickActionConfig,

  [GamepadLayout.global.activate]: {
    handler: () => activate(),
    repeat: false,
  } satisfies JoystickActionConfig,
  [GamepadLayout.global.toggleFullscreenVideo]: {
    handler: () => toggleFocusedCameraStreamFullscreen(),
    repeat: false,
  } satisfies JoystickActionConfig,
};

export const GamepadProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const router = useRouter();
  const isLayoutsByIdRoute = useRouterState({
    select: s => s.matches.some(m => m.routeId === '/(dashboard)/layouts/$id'),
  });
  const setCurrentLayoutIdInStorage = useLayoutStorage(s => s.setCurrentLayoutIdInStorage);
  const currentLayoutId = useLayoutStorage(s => s.currentLayoutId);
  const { allDbLayouts: dashboardLayouts } = useLayoutDBSync(PageNames.Dashboard);

  const orderedLayouts = useMemo(() => {
    if (!dashboardLayouts?.length) return [];
    return getLayoutsSortedByName(dashboardLayouts);
  }, [dashboardLayouts]);

  const handleChangeLayout = useCallback(
    (id: string) => {
      setCurrentLayoutIdInStorage(id);
      router.navigate({ to: '/' });
    },
    [setCurrentLayoutIdInStorage, router]
  );

  const switchToNextLayout = useCallback(() => {
    const id = getNextLayoutId(orderedLayouts, currentLayoutId);
    if (id) handleChangeLayout(id);
  }, [handleChangeLayout, orderedLayouts, currentLayoutId]);

  const switchToPreviousLayout = useCallback(() => {
    const id = getPreviousLayoutId(orderedLayouts, currentLayoutId);
    if (id) handleChangeLayout(id);
  }, [handleChangeLayout, orderedLayouts, currentLayoutId]);

  const layoutRouteInputMap = useMemo<JoystickConfigMap>(
    () => ({
      [GamepadLayout.layout.nextLayout]: {
        handler: () => switchToNextLayout(),
        repeat: false,
      } satisfies JoystickActionConfig,
      [GamepadLayout.layout.previousLayout]: {
        handler: () => switchToPreviousLayout(),
        repeat: false,
      } satisfies JoystickActionConfig,
      [GamepadLayout.layout.toggleCameraSelector]: {
        handler: () => toggleFocusedCameraSelector(),
        repeat: false,
      } satisfies JoystickActionConfig,
    }),
    [switchToNextLayout, switchToPreviousLayout]
  );

  const defaultGlobalMap = useMemo<JoystickConfigMap>(
    () =>
      isLayoutsByIdRoute
        ? { ...GLOBAL_INPUT_MAP, ...layoutRouteInputMap }
        : { ...GLOBAL_INPUT_MAP },
    [isLayoutsByIdRoute, layoutRouteInputMap]
  );

  const [globalMapOverride, setGlobalMapState] = useState<JoystickConfigMap | undefined>(undefined);
  const hasGlobalOverride = globalMapOverride !== undefined;
  const globalMapState = globalMapOverride ?? defaultGlobalMap;

  const setGlobalMap = useCallback(
    (map: JoystickConfigMap) => {
      setGlobalMapState(map);
    },
    [setGlobalMapState]
  );

  const clearGlobalMap = useCallback(() => {
    setGlobalMapState(undefined);
  }, []);

  const [widgetMaps, setWidgetMaps] = useState<Record<string, JoystickConfigMap>>({});
  const [activeWidgetId, setActiveWidgetId] = useState<string | null>(null);
  const [isGamepadConnected, setIsGamepadConnected] = useState<boolean>(false);
  const [gamepadId, setGamepadId] = useState<string>('');

  const registerWidgetMap = useCallback((widgetId: string, map: JoystickConfigMap) => {
    setWidgetMaps(prev => {
      if (prev[widgetId] === map) return prev;
      return { ...prev, [widgetId]: map };
    });
  }, []);

  const unregisterWidgetMap = useCallback((widgetId: string) => {
    setWidgetMaps(prev => {
      if (!Object.hasOwn(prev, widgetId)) return prev;
      const copy = { ...prev };
      delete copy[widgetId];
      return copy;
    });
  }, []);

  const activeWidgetMap = useMemo(
    () => (activeWidgetId ? widgetMaps[activeWidgetId] : undefined),
    [activeWidgetId, widgetMaps]
  );

  const effectiveStartMap = useMemo<JoystickConfigMap>(
    () => (hasGlobalOverride ? { ...globalMapState } : { ...globalMapState, ...activeWidgetMap }),
    [globalMapState, activeWidgetMap, hasGlobalOverride]
  );

  useGamepadControl({
    inputConfig: effectiveStartMap,
    axisThreshold: 0.4,
    setIsGamepadConnected,
    setGamepadId,
  });

  const value = useMemo<GamepadContextValue>(
    () => ({
      gamepadId,
      isGamepadConnected,
      setGlobalMap,
      clearGlobalMap,
      registerWidgetMap,
      unregisterWidgetMap,
      activeWidgetId,
      setActiveWidgetId,
    }),
    [
      gamepadId,
      isGamepadConnected,
      setGlobalMap,
      clearGlobalMap,
      registerWidgetMap,
      unregisterWidgetMap,
      activeWidgetId,
      setActiveWidgetId,
    ]
  );

  return <GamepadContext.Provider value={value}>{children}</GamepadContext.Provider>;
};
